import "./index.less";
import { useCallback, useImperativeHandle, useRef, useState } from "react";
import { queryDiscountCouponPage } from "../../api";
import VipCouponCard from "@/components/vip-coupon-card";
import { EmptyPlaceholder } from "@/components/placeholder";
import { trackGdp } from "@/utils/gio-report";
import { getReportVipData } from "../../utils";
import { useUserContext } from "../../routes/home/userContext";
import { CouponInfo } from "../../types";
import { useGoodsFocus } from "../tab-goods-content/GoodsFocusContext";
import PopupCom from "@/component/popupCom";
import React from "react";
import * as Sentry from "@sentry/react";

const RedPackPopup = React.forwardRef((props, ref) => {
  const [currentTab, setCurrentTab] = useState<"canuse" | "cannotuse">(
    "canuse",
  );
  const { currentGoodsList, focusedGoods } = useGoodsFocus();
  const cannotuseCouponistRef = useRef<HTMLDivElement | null>(null);
  const [canusePageNo, setCanusePageNo] = useState<number>(1);
  const [cannotusePageNo, setCannotusePageNo] = useState<number>(1);
  const [cannotuseListEnd, setCannotuseListEnd] = useState<boolean>(false);
  const [canuseList, setCanuseList] = useState<CouponInfo[]>([]);
  const [cannotuseList, setCannotuseList] = useState<CouponInfo[]>([]);
  const { levelInfo } = useUserContext();
  const RedPackRef = useRef<any>(null);

  const getCanuseRedPackData = useCallback(
    async (pageNo?: number) => {
      const redPackRes = await queryDiscountCouponPage({
        actCode: focusedGoods?.actCode || "",
        pageNo: pageNo || 1,
        pageSize: 10,
        state: 1,
      });
      let newlist = [];
      if (redPackRes?.code === 200) {
        newlist = redPackRes?.data?.coupons?.records;
        setCanuseList(canuseList.concat(newlist || []));
      } else {
        Sentry.captureException(new Error("获取可用红包列表失败"), {
          tags: {
            error_type: "getCanuseRedPackData_no_valid_data",
            biz: "charge-vip",
          },
        });
      }
    },
    [canuseList, focusedGoods?.actCode],
  );

  const onShow = () => {
    RedPackRef.current?.onOpen();
    init();
  };

  useImperativeHandle(ref, () => ({
    onShow,
  }));

  const getCannotuseRedPackData = useCallback(
    async (pageNo?: number) => {
      const redPackRes = await queryDiscountCouponPage({
        pageNo: pageNo || 1,
        pageSize: 10,
        state: 2,
      });
      if (redPackRes?.code === 200) {
        setCannotuseList(
          cannotuseList.length && pageNo
            ? cannotuseList.concat(redPackRes?.data?.coupons?.records || [])
            : redPackRes?.data?.coupons?.records || [],
        );
      } else {
        Sentry.captureException(new Error("获取不可用红包列表失败"), {
          tags: {
            error_type: "getCannotuseRedPackData_no_valid_data",
            biz: "charge-vip",
          },
        });
      }
      // 当返回的记录小于pageSize时，说明没有更多数据了
      if (
        redPackRes?.data?.coupons?.records?.length <
        redPackRes?.data?.coupons?.size
      ) {
        setCannotuseListEnd(true);
      }
    },
    [cannotuseList],
  );

  const getData = useCallback(() => {
    getCanuseRedPackData(canusePageNo);
    getCannotuseRedPackData(cannotusePageNo);
  }, [
    getCanuseRedPackData,
    canusePageNo,
    getCannotuseRedPackData,
    cannotusePageNo,
  ]);

  const init = useCallback(() => {
    getData();
    setCurrentTab("canuse");
    trackGdp("P11222", "B014", {
      ...getReportVipData(levelInfo),
      activityld: currentGoodsList
        ?.map((item) => item.actCode)
        ?.filter(Boolean)
        ?.join(","),
    });
  }, [getData, levelInfo, currentGoodsList]);

  const canuseCouponistRef = useRef<HTMLDivElement | null>(null);
  const onCanuseListScroll = (e) => {
    e?.stopPropagation();
    const listDom = canuseCouponistRef.current;
    if (listDom) {
      const isEnd =
        listDom.scrollTop + 50 >= listDom.scrollHeight - listDom.clientHeight;
      if (isEnd) {
        let nextPage = canusePageNo + 1;
        // 如果计算出来的数据量比已有的数据还多，说明上一次还没加载成功，重新来
        if (canusePageNo * 10 > canuseList?.length) {
          nextPage = canusePageNo;
          return;
        }
        setCanusePageNo(nextPage);
        getCanuseRedPackData(nextPage);
      }
    }
  };

  const reset = () => {
    setCannotusePageNo(1);
    setCanusePageNo(1);
    setCanuseList([]);
    setCannotuseList([]);
  };

  const onCannotuseListScroll = (e) => {
    e?.stopPropagation();
    const listDom = cannotuseCouponistRef.current;
    if (listDom) {
      const isEnd =
        listDom.scrollTop + 50 >= listDom.scrollHeight - listDom.clientHeight;
      if (isEnd) {
        if (cannotuseListEnd) {
          return;
        }
        let nextPage = cannotusePageNo + 1;
        // 如果计算出来的数据量比已有的数据还多，说明上一次还没加载成功，重新来
        if (cannotusePageNo * 10 > cannotuseList?.length) {
          nextPage = cannotusePageNo;
          return;
        }
        console.log("nextPage", nextPage);
        setCannotusePageNo(nextPage);
        getCannotuseRedPackData(nextPage);
      }
    }
  };
  return (
    <PopupCom
      ref={RedPackRef}
      title={"VIP抵扣红包"}
      onCloseCallBack={() => {
        reset();
      }}
      height={"75vh"}
    >
      <div className="red-pack-content">
        <div className="red-pack-tabs">
          <div
            key="canuse"
            className={`red-pack-tab ${currentTab === "canuse" ? "active-tab" : ""}`}
            onClick={() => {
              trackGdp("P11222", "B016", {
                tabType: "不可用红包",
                ...getReportVipData(levelInfo),
              });
              setCurrentTab("canuse");
            }}
          >
            可用红包
          </div>
          <div
            key="cannotuse"
            className={`red-pack-tab ${currentTab === "cannotuse" ? "active-tab" : ""}`}
            onClick={() => {
              trackGdp("P11222", "B016", {
                tabType: "可用红包",
                ...getReportVipData(levelInfo),
              });
              setCurrentTab("cannotuse");
            }}
          >
            不可用红包
          </div>
        </div>
        {currentTab === "canuse" ? (
          <div
            className="vip-coupon-list"
            key={"canuse-vip-coupon-list"}
            onScroll={onCanuseListScroll}
            ref={canuseCouponistRef}
          >
            {canuseList?.length ? (
              canuseList?.map((canuseItem, index) => {
                return (
                  <div key={`canuselist-${canuseItem?.couponId}-${index}`}>
                    <VipCouponCard
                      couponInfo={canuseItem}
                      className="vip-coupon"
                    />
                  </div>
                );
              })
            ) : (
              <EmptyPlaceholder text={"暂无可用卡券"}></EmptyPlaceholder>
            )}
          </div>
        ) : (
          <div
            className="vip-coupon-list"
            key={"cannotuse-vip-coupon-list"}
            onScroll={onCannotuseListScroll}
            ref={cannotuseCouponistRef}
          >
            {cannotuseList?.length ? (
              cannotuseList?.map((cannotuseItem, index) => {
                return (
                  <div
                    key={`cannotuselist-${cannotuseItem?.couponId}-${index}`}
                  >
                    <VipCouponCard
                      couponInfo={cannotuseItem}
                      disabled={true}
                      className="vip-coupon"
                    />
                  </div>
                );
              })
            ) : (
              <EmptyPlaceholder text={"暂无可用卡券"}></EmptyPlaceholder>
            )}
          </div>
        )}

        {currentTab === "canuse" && !!canuseList?.length && (
          <div className="red-pack-list-tip">展示近半年的VIP抵扣红包～ </div>
        )}
        {currentTab === "cannotuse" && !!cannotuseList?.length && (
          <div className="red-pack-list-tip">展示近半年的VIP抵扣红包～ </div>
        )}
      </div>
    </PopupCom>
  );
});
export default RedPackPopup;
